"use client";

/**
 * Full-screen photo gallery, drawn by PhotoSwipe 5: one photo at a time, moved
 * by a swipe, the arrows or the keyboard, and zoomed by a pinch, a double tap or
 * the wheel. Under it the thumbnails of the whole set, the one on show outlined,
 * and a button in the top bar swaps to all of them down one scrolling page. A
 * photo from a trail says which, over the photo.
 *
 * A photo marked `video` is a slide that plays in place: its path is an mp4 and
 * its thumbnail a frame of it, shown until play is pressed, so nothing of the
 * video is fetched before. Opened on a video, the gallery starts it; reached by
 * a swipe, it waits to be pressed. Leaving the slide pauses it.
 *
 * PhotoSwipe needs a photo's size before it loads it. Where the API gives none,
 * it is read off the thumbnail, which keeps the photo's shape, and the photo is
 * redrawn once known; the first is waited for, so the gallery does not open on
 * a guess. The zoom levels are relative to the fitted photo, so a width guessed
 * wrong changes nothing on screen.
 *
 * The browser's back button closes it, as it does a sheet: opening adds a
 * history entry at the same address, keeping its state.
 *
 * openGallery(photos, index, options) opens it from anywhere; Gallery is the
 * same API as an object, for whoever prefers one. Three options are for the
 * photos the people going to an event have added (A minha inscrição):
 *  - caption(photo): what is said over a photo in place of its trail -- who
 *    added it (uploaderCaption)
 *  - downloadUrl(photo): where the photo is served as a download, for a button
 *    in the top bar; a same-origin address answering with an attachment. A
 *    video has no such button, and is never the cover
 *  - cover: {current(), canPick, onPick(photo, use)} for an event's album: the
 *    photo that is the cover says so over its bottom right, and whoever may
 *    pick it has the button there that makes the photo on show the cover, or
 *    stops it being so. current() answers the id of the cover
 */
import PhotoSwipe from "photoswipe";
import "photoswipe/style.css";
import "./gallery.css";
import { cdnUrl } from "@/lib/cdn";
import { escapeHtml } from "@/lib/format";
import type { Photo } from "@/lib/types/event";

// the row of thumbnails under the photo, which the photo is fitted above
const STRIP_HEIGHT = 76;
// what the backoffice resizes an upload to, for a size read off a thumbnail
const PHOTO_WIDTH = 1024;
// how long the first photo's thumbnail is waited for before opening anyway
const SIZE_WAIT_MS = 800;

export type GalleryCaption = { icon?: "trail" | "user" | "cover" | null; text: string };

export type GalleryCover = {
  /** the id of the album's cover, or null for the banner */
  current: () => number | string | null;
  canPick: boolean;
  /** make `photo` the cover (use true) or stop it being so; answers once done */
  onPick: (photo: Photo, use: boolean) => Promise<unknown> | unknown;
};

export type GalleryOptions = {
  caption?: (photo: Photo) => GalleryCaption | string | null | undefined;
  downloadUrl?: (photo: Photo) => string;
  cover?: GalleryCover | null;
};

type Size = { width: number; height: number };

const photoUrl = (photo: Photo) => cdnUrl(photo.path);
const thumbUrl = (photo: Photo) => cdnUrl(photo.thumbnail || photo.path);

const ICONS: Record<NonNullable<GalleryCaption["icon"]>, string> = {
  trail:
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m8 3 4 8 5-5 5 15H2L8 3z"/></svg>',
  user: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>',
  cover:
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-5-5L5 21"/></svg>',
};

const captionHtml = (caption: GalleryCaption | string | null | undefined) => {
  if (!caption) return "";
  const data = typeof caption === "string" ? { text: caption } : caption;
  if (!data.text) return "";
  return `<span class="gallery-caption">${data.icon ? ICONS[data.icon] : ""}<span>${escapeHtml(data.text)}</span></span>`;
};

/** the trail a photo was taken on, over it */
export const trailCaption = (photo: Photo): GalleryCaption | null => (photo.trail_title ? { icon: "trail", text: photo.trail_title } : null);

/** who added a photo, for the photos the people going have added */
export const uploaderCaption = (photo: Photo): GalleryCaption | null => (photo.uploader_name ? { icon: "user", text: photo.uploader_name } : null);

// the icons of the top bar's own buttons
const DOWNLOAD_ICON =
  '<svg aria-hidden="true" class="pswp__icn" viewBox="0 0 32 32" width="32" height="32"><path d="M16 6v13m0 0-5-5m5 5 5-5M8 24h16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
// over the thumbnail of a video, in the strip and down the page
const PLAY_BADGE =
  '<span class="gallery-play"><svg aria-hidden="true" viewBox="0 0 24 24" width="14" height="14"><path d="M8 5.5v13l11-6.5z" fill="currentColor"/></svg></span>';
// the foot of a video, where its own controls are: a drag there moves the
// video's position, not the gallery to the next slide
const VIDEO_CONTROLS_HEIGHT = 70;

const isVideoEl = (target: unknown): target is HTMLVideoElement => target instanceof HTMLVideoElement;

const GRID_ICON =
  '<svg aria-hidden="true" class="pswp__icn" viewBox="0 0 32 32" width="32" height="32"><rect x="7" y="7" width="8" height="8" rx="1.5" fill="currentColor"/><rect x="17" y="7" width="8" height="8" rx="1.5" fill="currentColor"/><rect x="7" y="17" width="8" height="8" rx="1.5" fill="currentColor"/><rect x="17" y="17" width="8" height="8" rx="1.5" fill="currentColor"/></svg>';
const SINGLE_ICON =
  '<svg aria-hidden="true" class="pswp__icn" viewBox="0 0 32 32" width="32" height="32"><rect x="7" y="7" width="18" height="18" rx="2" fill="none" stroke="currentColor" stroke-width="2.4"/><circle cx="13" cy="13" r="2" fill="currentColor"/><path d="m25 20-5-5-10 10" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// PhotoSwipe takes every wheel turn inside the gallery for itself, to pan or
// zoom the photo; a strip or a page of photos has to scroll instead
const keepScrolling = (el: HTMLElement) => el.addEventListener("wheel", (e) => e.stopPropagation());

let current: GalleryInstance | null = null;

class GalleryInstance {
  photos: Photo[];
  options: Required<Pick<GalleryOptions, "caption">> & GalleryOptions;
  sizes: (Size | null)[];
  pswp: PhotoSwipe | null = null;
  destroyed = false;
  gridOpen = false;
  grid: HTMLElement | null = null;
  coverHolder: HTMLElement | null = null;
  pickingCover = false;

  constructor(photos: Photo[], options: GalleryOptions) {
    this.photos = photos;
    this.options = { caption: trailCaption, ...options };
    this.sizes = photos.map((photo) => (photo.width && photo.height ? { width: Number(photo.width), height: Number(photo.height) } : null));
  }

  async show(start: number) {
    const sized = this.photos.map((photo, i) => this.sizeFromThumbnail(photo, i));
    await Promise.race([sized[start], new Promise((resolve) => setTimeout(resolve, SIZE_WAIT_MS))]);
    if (this.destroyed) {
      return;
    }
    this.open(start);
  }

  /** resolves once the thumbnail has loaded or failed, having taken the photo's size from it when the API gave none */
  sizeFromThumbnail(photo: Photo, index: number) {
    return new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (!this.sizes[index] && img.naturalWidth) {
          this.sizes[index] = { width: PHOTO_WIDTH, height: Math.round((PHOTO_WIDTH * img.naturalHeight) / img.naturalWidth) };
          this.pswp?.refreshSlideContent(index);
        }
        resolve();
      };
      img.onerror = () => resolve();
      img.src = thumbUrl(photo);
    });
  }

  open(start: number) {
    const photos = this.photos;
    const several = photos.length > 1;

    const pswp = (this.pswp = new PhotoSwipe({
      dataSource: photos.map((photo) =>
        photo.video ? { type: "video", videoSrc: photoUrl(photo), msrc: thumbUrl(photo), loop: !!photo.loop } : { src: photoUrl(photo), msrc: thumbUrl(photo) },
      ),
      index: start,
      bgOpacity: 1,
      showHideAnimationType: "fade",
      paddingFn: () => ({ top: 0, left: 0, right: 0, bottom: several ? STRIP_HEIGHT : 0 }),
      initialZoomLevel: "fit",
      secondaryZoomLevel: (zoom) => zoom.fit * 2.5,
      maxZoomLevel: (zoom) => zoom.fit * 5,
      imageClickAction: "zoom",
      closeTitle: "Fechar",
      zoomTitle: "Ampliar",
      arrowPrevTitle: "Anterior",
      arrowNextTitle: "Seguinte",
      errorMsg: "Não foi possível carregar a fotografia",
    }));

    // a size found since the slide was made, or a guess until then; a portrait
    // guess would open a landscape photo as a sliver
    pswp.addFilter("itemData", (data, index) => ({
      ...data,
      ...(this.sizes[index] || { width: PHOTO_WIDTH, height: Math.round((PHOTO_WIDTH * 3) / 4) }),
    }));

    this.playVideos(pswp, photos[start].video ? start : -1);

    pswp.on("uiRegister", () => {
      pswp.ui?.registerElement({
        name: "caption",
        appendTo: "root",
        onInit: (el) => {
          el.classList.add("gallery-caption-holder");
          el.classList.toggle("single", !several);
          pswp.on("change", () => {
            el.innerHTML = captionHtml(this.options.caption(photos[pswp.currIndex]));
            // over a video the caption moves up, off the video's controls
            pswp.element?.classList.toggle("pswp--video", !!photos[pswp.currIndex].video);
          });
        },
      });
      if (this.options.downloadUrl) {
        const downloadUrl = this.options.downloadUrl;
        pswp.ui?.registerElement({
          name: "download",
          order: 8,
          isButton: true,
          tagName: "a",
          title: "Descarregar",
          html: DOWNLOAD_ICON,
          onInit: (el) => {
            el.setAttribute("download", "");
            pswp.on("change", () => {
              const shown = photos[pswp.currIndex];
              // a video is too large to be handed over as a download
              el.style.display = shown.video ? "none" : "";
              if (!shown.video) {
                (el as HTMLAnchorElement).href = downloadUrl(shown);
              }
            });
          },
        });
      }
      if (this.options.cover) {
        pswp.ui?.registerElement({
          name: "cover",
          appendTo: "root",
          onInit: (el) => {
            el.classList.add("gallery-cover-holder");
            el.classList.toggle("single", !several);
            this.coverHolder = el;
            el.addEventListener("click", (e) => {
              if ((e.target as HTMLElement).closest(".gallery-cover-pick")) {
                this.pickCover();
              }
            });
            pswp.on("change", () => this.drawCover());
          },
        });
      }
      if (several) {
        pswp.ui?.registerElement({
          name: "all-photos",
          order: 9,
          isButton: true,
          title: "Ver todas",
          html: GRID_ICON,
          onClick: () => this.toggleGrid(),
        });
        pswp.ui?.registerElement({ name: "strip", appendTo: "root", onInit: (el) => this.buildStrip(el) });
        pswp.ui?.registerElement({
          name: "grid",
          appendTo: "root",
          onInit: (el) => {
            this.grid = el;
            keepScrolling(el);
          },
        });
      }
    });

    // the keyboard belongs to the page of photos while it is open, and Escape
    // takes it away rather than closing the gallery
    pswp.on("keydown", (e) => {
      if (this.gridOpen) {
        e.preventDefault();
        if (e.originalEvent.key === "Escape") {
          this.toggleGrid();
        }
      }
    });

    this.followHistory(pswp);
    pswp.on("destroy", () => {
      this.pswp = null;
      this.destroyed = true;
      if (current === this) {
        current = null;
      }
    });
    pswp.init();
  }

  /**
   * A video's slide: the video itself in place of PhotoSwipe's image, with the
   * browser's own controls. `autoplay` is the slide to start by itself, the one
   * the gallery was opened on, or -1.
   */
  playVideos(pswp: PhotoSwipe, autoplay: number) {
    let toStart = autoplay;

    pswp.on("contentLoad", (e) => {
      const { content } = e;
      if (content.data.type !== "video") return;
      e.preventDefault();
      if (content.element) return;
      const video = document.createElement("video");
      video.className = "pswp__video";
      video.controls = true;
      video.playsInline = true;
      // the frame is all that is fetched until play is pressed
      video.preload = "none";
      video.poster = String(content.data.msrc || "");
      // a flyover has no sound: it plays muted, which also lets it start by itself, and over again
      if (content.data.loop) {
        video.muted = true;
        video.loop = true;
      }
      video.src = String(content.data.videoSrc);
      // PhotoSwipe types a slide's element as an image or a div
      content.element = video as unknown as HTMLDivElement;
      content.onLoaded();
    });
    // the poster is the video's own; an image under it would show through its edges
    pswp.addFilter("useContentPlaceholder", (use, content) => (content.data.type === "video" ? false : use));
    pswp.addFilter("isContentZoomable", (zoomable, content) => (content.data.type === "video" ? false : zoomable));

    pswp.on("contentActivate", ({ content }) => {
      if (isVideoEl(content.element) && content.index === toStart) {
        toStart = -1;
        // a browser may refuse to start a video by itself: the play button is there
        content.element.play().catch(() => {});
      }
    });
    pswp.on("contentDeactivate", ({ content }) => {
      if (isVideoEl(content.element)) {
        content.element.pause();
      }
    });
    // closing fades the gallery out before it destroys its slides
    pswp.on("close", () => {
      const shown = pswp.currSlide?.content.element;
      if (isVideoEl(shown)) {
        shown.pause();
      }
    });
    pswp.on("contentDestroy", ({ content }) => {
      if (isVideoEl(content.element)) {
        content.element.pause();
        content.element.removeAttribute("src");
        content.element.load();
      }
    });

    // PhotoSwipe takes a press on a slide for itself: over a video it is the
    // video's, and along its foot so is a drag
    pswp.addFilter("preventPointerEvent", (prevent, e) => (isVideoEl(e.target) ? false : prevent));
    pswp.on("pointerDown", (e) => {
      const target = e.originalEvent.target;
      if (isVideoEl(target) && e.originalEvent.clientY > target.getBoundingClientRect().bottom - VIDEO_CONTROLS_HEIGHT) {
        e.preventDefault();
      }
    });
    pswp.on("tapAction", (e) => {
      if (isVideoEl(e.originalEvent.target)) {
        e.preventDefault();
      }
    });
  }

  /** what the photo on show is to the album's cover: nothing for one that is not it, shown to someone who cannot pick it */
  drawCover() {
    const cover = this.options.cover;
    if (!cover || !this.pswp || !this.coverHolder) return;
    const photo = this.photos[this.pswp.currIndex];
    // a video is never an album's cover
    if (photo.video) {
      this.coverHolder.innerHTML = "";
      return;
    }
    const isCurrent = String(cover.current()) === String(photo.id);
    this.coverHolder.innerHTML = cover.canPick
      ? `<div role="button" class="gallery-cover-pick${isCurrent ? " current" : ""}">${isCurrent ? "Deixar de ser capa" : `${ICONS.cover} Usar como capa`}</div>`
      : isCurrent
        ? `<span class="gallery-caption">${ICONS.cover}<span>Capa deste álbum</span></span>`
        : "";
  }

  async pickCover() {
    const cover = this.options.cover;
    if (!cover || !this.pswp || this.pickingCover) return;
    const photo = this.photos[this.pswp.currIndex];
    this.pickingCover = true;
    try {
      await cover.onPick(photo, String(cover.current()) !== String(photo.id));
    } finally {
      this.pickingCover = false;
    }
    if (this.pswp) {
      this.drawCover();
    }
  }

  /** every photo as a thumbnail in one sideways row, the one on show outlined and kept in view; a tap on one goes to it */
  buildStrip(el: HTMLElement) {
    const photos = this.photos;
    el.innerHTML = photos
      .map(
        (photo, i) =>
          `<div role="button" class="gallery-thumb" data-index="${i}" title="${photo.video ? "Vídeo" : "Fotografia"} ${i + 1}"><img src="${escapeHtml(thumbUrl(photo))}" alt="" loading="lazy" draggable="false" />${photo.video ? PLAY_BADGE : ""}</div>`,
      )
      .join("");
    keepScrolling(el);
    el.addEventListener("click", (e) => {
      const thumb = (e.target as HTMLElement).closest<HTMLElement>(".gallery-thumb");
      if (thumb && this.pswp) {
        this.pswp.goTo(Number(thumb.dataset.index));
      }
    });

    let first = true;
    this.pswp?.on("change", () => {
      if (!this.pswp) return;
      el.querySelector(".gallery-thumb.current")?.classList.remove("current");
      const thumb = el.children[this.pswp.currIndex] as HTMLElement | undefined;
      if (thumb) {
        thumb.classList.add("current");
        // the gallery is not laid out yet as the first photo is set
        const smooth = !first;
        first = false;
        requestAnimationFrame(() =>
          el.scrollTo({ left: thumb.offsetLeft - (el.clientWidth - thumb.offsetWidth) / 2, behavior: smooth ? "smooth" : "auto" }),
        );
      }
    });
  }

  /** swaps between the one photo and every photo down a page, built the first time and opened on the photo showing */
  toggleGrid() {
    const pswp = this.pswp;
    if (!pswp || !pswp.element || !this.grid) return;
    this.gridOpen = !this.gridOpen;
    pswp.element.classList.toggle("gallery-grid-open", this.gridOpen);

    const button = pswp.element.querySelector<HTMLElement>(".pswp__button--all-photos");
    if (button) {
      button.innerHTML = this.gridOpen ? SINGLE_ICON : GRID_ICON;
      button.title = this.gridOpen ? "Ver uma a uma" : "Ver todas";
    }
    if (!this.gridOpen) {
      return;
    }
    // the top bar may have been tapped away, and it holds the way back
    pswp.element.classList.add("pswp--ui-visible");

    if (!this.grid.children.length) {
      this.grid.innerHTML = this.photos
        .map((photo, i) => {
          const size = this.sizes[i];
          const ratio = size ? `style="aspect-ratio: ${size.width} / ${size.height}"` : "";
          return `<div role="button" class="gallery-grid-photo" data-index="${i}"><img src="${escapeHtml(photo.video ? thumbUrl(photo) : photoUrl(photo))}" alt="" loading="lazy" draggable="false" ${ratio} />${photo.video ? PLAY_BADGE : ""}${captionHtml(this.options.caption(photo))}</div>`;
        })
        .join("");
      this.grid.addEventListener("click", (e) => {
        const photo = (e.target as HTMLElement).closest<HTMLElement>(".gallery-grid-photo");
        if (photo) {
          this.toggleGrid();
          pswp.goTo(Number(photo.dataset.index));
        }
      });
    }
    const shown = this.grid.children[pswp.currIndex] as HTMLElement | undefined;
    if (shown) {
      this.grid.scrollTop = shown.offsetTop - 60;
    }
  }

  /** opening adds a history entry at the same address, keeping its state, so the back button closes the gallery */
  followHistory(pswp: PhotoSwipe) {
    window.history.pushState(window.history.state, "", window.location.href);
    let closedByHistory = false;
    const onPopState = () => {
      closedByHistory = true;
      pswp.close();
    };
    window.addEventListener("popstate", onPopState);
    pswp.on("destroy", () => {
      window.removeEventListener("popstate", onPopState);
      if (!closedByHistory) {
        window.history.back();
      }
    });
  }

  close() {
    this.destroyed = true;
    this.pswp?.close();
  }
}

/**
 * Opens the gallery on these photos, at `index`. Nothing for an empty set.
 */
export const openGallery = (photos: Photo[], index = 0, options: GalleryOptions = {}) => {
  if (typeof window === "undefined" || !photos || !photos.length) {
    return;
  }
  current?.close();
  const start = Math.min(Math.max(index || 0, 0), photos.length - 1);
  current = new GalleryInstance(photos, options);
  current.show(start);
};

export const closeGallery = () => current?.close();

/** the gallery as an object, for whoever prefers one: Gallery.open(photos, index, options) */
export const Gallery = { open: openGallery, close: closeGallery, trailCaption, uploaderCaption };
